import { Transform, Type } from 'class-transformer';
import { find, sortBy } from 'lodash';

import { booleanTransform, cent2double, convertFloatNumber, convertIntNumber, dateTransform } from '../utils/helper';

export class UserStatsRequest {
	hqid!: number;
	oid?: number;
	campid?: number;
	@Transform(dateTransform)
	startdate?: Date;
	@Transform(dateTransform)
	stopdate?: Date;
	@Transform(booleanTransform)
	dayly?: boolean = false;

	manageDaily(): void {
		if (this.dayly) {
			this.startdate = undefined;
			this.stopdate = undefined;
		}
	}
}

export class UserStats {
	@Type(() => UserStatsRiepilogo)
	riepilogo!: UserStatsRiepilogo[];
	@Type(() => UserStatsRiepilogo)
	riepilogoAmount!: UserStatsRiepilogo[];
	@Type(() => UserStatsEsiti)
	esiti?: UserStatsEsiti[];
	@Type(() => UserStatsEsitiConsegne)
	esiticonsegne?: UserStatsEsitiConsegne[];
	@Type(() => UserStatsEsitiConsegne)
	amountconsegne?: UserStatsEsitiConsegne[];

	get riepilogoTotal(): number {
		if (!this.riepilogo?.length) return 0;
		return this.riepilogo.reduce((acc: number, a: UserStatsRiepilogo) => acc + convertIntNumber(a.valore), 0);
	}

	get chiaviRiepilogo(): string[] {
		return this.riepilogo.map((r: UserStatsRiepilogo) => r.chiave as string);
	}

	get esitiSorted(): UserStatsEsiti[] {
		return sortBy(this.esiti, ['name']);
	}

	get esitiConsegneSorted(): UserStatsEsitiConsegne[] {
		return sortBy(this.esiticonsegne, ['name']);
	}

	get riepilogoValues(): any {
		const data: any = {};
		for (const key of this.chiaviRiepilogo) {
			data[key] = convertIntNumber(find(this.riepilogo, ['chiave', key])?.valore);
		}
		return data;
	}

	get riepilogoAmountValues(): any {
		const data: any = {};
		for (const key of this.chiaviRiepilogo) {
			data[key] = cent2double(convertFloatNumber(find(this.riepilogoAmount, ['chiave', key])?.valore));
		}
		return data;
	}

	get riepilogoAmountValuesTotal(): any {
		let sum = 0;
		for (const key of this.chiaviRiepilogo) {
			sum += cent2double(convertFloatNumber(find(this.riepilogoAmount, ['chiave', key])?.valore))!;
		}
		return sum;
	}

	get totalsEsiti(): any {
		if (!this.esiti?.length) return {};
		const esito = this.esiti[0];
		const data: any = {};
		for (const key in esito) {
			if (key && key !== 'name') {
				data[key] = this.esiti.reduce((acc: number, a: any) => acc + a[key], 0);
			}
		}
		return data;
	}

	get totalsEsitiConsegne(): any {
		if (!this.esiticonsegne?.length) return {};
		const esito = this.esiticonsegne[0];
		const data: any = {};
		for (const key in esito) {
			if (key && key !== 'name') {
				data[key] = this.esiticonsegne.reduce((acc: number, a: any) => acc + a[key], 0);
			}
		}
		return data;
	}

	get esitiReject(): any {
		const data: any = {};
		const rejected = (this.esiticonsegne?.filter(e => e.name === 'rejected')[0] as any);
		for (const key of this.chiaviRiepilogo) {
			let mapkey = '';
			if (key === 'new') mapkey = 'nuovi';
			else if (key === 'portfolio') mapkey = 'portafogli';
			else if (key === 'dormient') mapkey = 'dormienti';
			else mapkey = key;
			data[key] = rejected ? rejected[mapkey] : 0;
		}
		data['total'] = rejected ? rejected['total'] : 0;
		return data;
	}

	get amountReject(): any {
		const data: any = {};
		const rejected = (this.amountconsegne?.filter(e => e.name === 'rejected')[0] as any);
		for (const key of this.chiaviRiepilogo) {
			let mapkey = '';
			if (key === 'new') mapkey = 'nuovi';
			else if (key === 'portfolio') mapkey = 'portafogli';
			else if (key === 'dormient') mapkey = 'dormienti';
			else mapkey = key;
			data[key] = rejected ? cent2double(rejected[mapkey]) : 0;
		}
		data['total'] = rejected ? cent2double(rejected['total']) : 0;
		return data;
	}

}

export class UserStatsEsito {
	dormienti?: number;
	nuovi?: number;
	portafogli?: number;
	total!: number;
}

export class UserStatsEsiti extends UserStatsEsito {
	name!: UserStatsCallStatus;

	get isReject(): boolean { return this.name === UserStatsCallStatus.REJECT; }
	get isRecall(): boolean { return this.name === UserStatsCallStatus.RECALL; }
	get isAccept(): boolean { return this.name === UserStatsCallStatus.ACCEPT; }
}

export class UserStatsEsitiConsegne extends UserStatsEsito {
	name!: UserStatsCallStatusDelivery;

	get isRejected(): boolean { return this.name === UserStatsCallStatusDelivery.REJECTED; }
	get isPending(): boolean { return this.name === UserStatsCallStatusDelivery.PENDING; }
	get isDelivered(): boolean { return this.name === UserStatsCallStatusDelivery.DELIVERED; }
}

export class UserStatsRiepilogo {
	chiave?: string;
	valore?: string;
}

export enum UserStatsCallStatus {
	REJECT = 'reject',
	RECALL = 'recall',
	ACCEPT = 'accept'
}

export enum UserStatsCallStatusDelivery {
	REJECTED = 'rejected',
	PENDING = 'pending',
	DELIVERED = 'delivered'
}